import type { RefObject } from "react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { MIN_CONTENT_WIDTH_PX, PAGE_HEADER_SLOT } from "./constants";

type PageHeaderActionsFit = {
  rowRef: RefObject<HTMLDivElement | null>;
  collapsed: boolean;
};

function findHeader(el: HTMLElement): HTMLElement | null {
  let node: Node | null = el;

  while (node) {
    if (node instanceof HTMLElement) {
      const header = node.closest<HTMLElement>(
        `[data-slot="${PAGE_HEADER_SLOT}"]`,
      );

      if (header) {
        return header;
      }
    }

    const root = node.getRootNode();

    node = root instanceof ShadowRoot ? root.host : null;
  }

  return null;
}

export function usePageHeaderActionsFit(): PageHeaderActionsFit {
  const rowRef = useRef<HTMLDivElement>(null);
  const naturalRef = useRef(0);
  const collapsedRef = useRef(false);

  const [collapsed, setCollapsed] = useState(false);

  const measure = useCallback(() => {
    const row = rowRef.current;

    if (!row) {
      return;
    }

    const header = findHeader(row);

    if (!header || header.clientWidth === 0) {
      return;
    }

    if (!collapsedRef.current) {
      naturalRef.current = row.scrollWidth;
    }

    const next = header.clientWidth - naturalRef.current < MIN_CONTENT_WIDTH_PX;

    if (next !== collapsedRef.current) {
      collapsedRef.current = next;

      setCollapsed(next);
    }
  }, []);

  useLayoutEffect(measure);

  useEffect(() => {
    const row = rowRef.current;
    const header = row && findHeader(row);

    if (!header) {
      return;
    }

    const observer = new ResizeObserver(measure);

    observer.observe(header);

    return () => observer.disconnect();
  }, [measure]);

  return { rowRef, collapsed };
}
